import * as React from 'react';
import { CSSProperties } from 'react';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { CellIndex, LayoutSize } from 'types';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { isLive, isPlaying, isMute, getLayout } from 'selectors/monitoringSelectors';
import { State } from 'reducers';
import { Bell, DoubleDownChevrons, ExclamationMark, LightningBolt, Pause, RightChevron, RightTriangle, StepForward, VolumeMute, VolumeUnmute } from '../../../../shapes';
import { seek, videoPlay, videoPause, videoMute, videoUnmute } from 'actions/monitoringActions';
import { Fade } from '../../../../transitions';

import './PlaybackControls.css';

type ShowWhenPausedProps = {
    paused: boolean;
};

const ShowWhenPaused: React.SFC<ShowWhenPausedProps> = ({ paused, children }) =>
    <Fade visible={ paused } duration={ 200 } element="span">
        { children }
    </Fade>
;

type Props = OwnProps & StateProps & DispProps & TranslateProps & RecomposeProps;

export const RawPlaybackControls = ({ cellIndex, live, playing, mute, buttonSize, t, styles, ...props }: Props) =>
    <div className="playback-controls-host" style={ styles.host }>
        <div className="playback-controls">
            <ShowWhenPaused paused={ !playing }>
                <button id={ `prev-alarm-button-${cellIndex}` } type="button" title={ t('704') } onClick={ props.onSeekPrevAlarm }>
                    <Bell width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
                <button id={ `prev-motion-button-${cellIndex}` } type="button" title={ t('703') } onClick={ props.onSeekPrevActivity }>
                    <ExclamationMark width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
                <button id={ `back10-button-${cellIndex}` } type="button" title={ t('702') } onClick={ props.onSeekPrev10Seconds }>
                    <DoubleDownChevrons width={ buttonSize } height={ buttonSize } style={ styles.shape270 }/>
                </button>
                <button id={ `back1-button-${cellIndex}` } type="button" title={ t('701') } onClick={ props.onSeekPrev1Second }>
                    <RightChevron width={ buttonSize } height={ buttonSize } style={ styles.shape180 }/>
                </button>
                <button id={ `prev-frame-button-${cellIndex}` } type="button" title={ t('2801') } onClick={ props.onSeekPrevFrame }>
                    <StepForward width={ buttonSize } height={ buttonSize } style={ styles.shape180 }/>
                </button>
            </ShowWhenPaused>
            <div style={ styles.playPauseHost }>
                <Fade visible={ !playing } duration={ 200 } style={ styles.playButton }>
                    <button id={ `play-button-${cellIndex}` } type="button" title={ t('TOOLTIP_BUTTON_PLAY') } onClick={ props.onPlay }>
                        <RightTriangle width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                    </button>
                </Fade>
                <Fade visible={ playing } duration={ 200 } style={ styles.pauseButton }>
                    <button id={ `pause-button-${cellIndex}` } type="button" title={ t('TOOLTIP_BUTTON_PAUSE') } onClick={ props.onPause }>
                        <Pause width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                    </button>
                </Fade>
            </div>
            <ShowWhenPaused paused={ !playing }>
                <button id={ `next-frame-button-${cellIndex}` } type="button" title={ t('2802') } onClick={ props.onSeekNextFrame }>
                    <StepForward width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
                <button id={ `forward1-button-${cellIndex}` } type="button" title={ t('705') } onClick={ props.onSeekNext1Second }>
                    <RightChevron width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
                <button id={ `forward10-button-${cellIndex}` } type="button" title={ t('706') } onClick={ props.onSeekNext10Seconds }>
                    <DoubleDownChevrons width={ buttonSize } height={ buttonSize } style={ styles.shape90 }/>
                </button>
                <button id={ `next-motion-button-${cellIndex}` } type="button" title={ t('707') } onClick={ props.onSeekNextActivity }>
                    <ExclamationMark width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
                <button id={ `next-alarm-button-${cellIndex}` } type="button" title={ t('708') } onClick={ props.onSeekNextAlarm }>
                    <Bell width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
            </ShowWhenPaused>
            <Fade visible={ !live || !playing } duration={ 200 } element="span">
                <button id={ `live-button-${cellIndex}` } type="button" title={ t('1071') } onClick={ props.onGoLive } style={ styles.liveButton }>
                    <LightningBolt width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
            </Fade>
            
            <Fade visible={ !mute } duration={ 200 } style={ styles.muteButton }>
                <button id={ `mute-button-${cellIndex}` } type="button" title={ t('AudioMuteCamera') } onClick={ props.onMute }>
                    <VolumeMute width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
            </Fade>
            <Fade visible={ mute } duration={ 200 } style={ styles.unmuteButton }>
                <button id={ `unmute-button-${cellIndex}` } type="button" title={ t('AudioUnMuteCamera') } onClick={ props.onUnmute }>
                    <VolumeUnmute width={ buttonSize } height={ buttonSize } style={ styles.shape }/>
                </button>
            </Fade>
            
        </div>
    </div>
;

export type OwnProps = {
    cellIndex: CellIndex;
};
type StateProps = {
    playing: boolean;
    live: boolean;
    mute: boolean;
    buttonSize: string;
};
type DispProps = {
    onPause: () => void;
    onPlay: () => void;
    onMute: () => void;
    onUnmute: () => void;
    onSeekPrev1Second: () => void;
    onSeekNext1Second: () => void;
    onSeekPrev10Seconds: () => void;
    onSeekNext10Seconds: () => void;
    onSeekPrevFrame: () => void;
    onSeekNextFrame: () => void;
    onSeekPrevActivity: () => void;
    onSeekNextActivity: () => void;
    onSeekPrevAlarm: () => void;
    onSeekNextAlarm: () => void;
    onGoLive: () => void;
};
type RecomposeProps = {
    styles: { [name: string]: CSSProperties };
};

const getButtonSize = (size: LayoutSize) => {
    switch (size) {
        case 9:
            return '.8em';
        case 16:
            return '.6em';
        default:
            return '1em';
    }
};

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => ({
    playing: isPlaying(state, ownProps),
    live: isLive(state, ownProps),
    mute: isMute(state, ownProps),
    buttonSize: getButtonSize(getLayout(state)),
});

const mapDispatchToProps = (dispatch: Function, { cellIndex }: OwnProps): DispProps => ({
    onPause: () => dispatch(videoPause(cellIndex)),
    onPlay: () => dispatch(videoPlay({ cellIndex, live: false })),
    onMute: () => dispatch(videoMute(cellIndex)),
    onUnmute: () => dispatch(videoUnmute(cellIndex)),
    onSeekPrev1Second: () => dispatch(seek({ cellIndex, seekType: 'prev-1sec' })),
    onSeekNext1Second: () => dispatch(seek({ cellIndex, seekType: 'next-1sec' })),
    onSeekPrev10Seconds: () => dispatch(seek({ cellIndex, seekType: 'prev-10sec' })),
    onSeekNext10Seconds: () => dispatch(seek({ cellIndex, seekType: 'next-10sec' })),
    onSeekPrevFrame: () => dispatch(seek({ cellIndex, seekType: 'prev-frame' })),
    onSeekNextFrame: () => dispatch(seek({ cellIndex, seekType: 'next-frame' })),
    onSeekPrevActivity: () => dispatch(seek({ cellIndex, seekType: 'prev-activity' })),
    onSeekNextActivity: () => dispatch(seek({ cellIndex, seekType: 'next-activity' })),
    onSeekPrevAlarm: () => dispatch(seek({ cellIndex, seekType: 'prev-alarm' })),
    onSeekNextAlarm: () => dispatch(seek({ cellIndex, seekType: 'next-alarm' })),
    onGoLive: () => dispatch(videoPlay({ cellIndex, live: true })),
});

export const PlaybackControls = compose<OwnProps & StateProps & DispProps & RecomposeProps & TranslateProps, OwnProps>(
    skin,
    translate(),
    connect(mapStateToProps, mapDispatchToProps),
    withProps(({ styles }: OemProps) => ({
        styles: {
            host: {
                backgroundColor: styles.Input.backgroundColor,
            },
            shape: {
                fill: styles.Window.color,
            },
            shape90: {
                fill: styles.Window.color,
                transform: 'rotate(90deg)',
            },
            shape180: {
                fill: styles.Window.color,
                transform: 'rotate(180deg)',
            },
            shape270: {
                fill: styles.Window.color,
                transform: 'rotate(270deg)',
            },
            playPauseHost: {
                position: 'relative',
                display: 'inline',
            },
            playButton: {
                position: 'absolute',
                display: 'inline',
            },
            pauseButton: {
                display: 'inline',
            },
            liveButton: {
                position: 'absolute',
                right: '3em',
            },
            muteButton: {
                position: 'absolute',
                display: 'inline',
                right: '1em',
            },
            unmuteButton: {
                position: 'absolute',
                display: 'inline',
                right: '1em',
            },
        },
    })),
)(RawPlaybackControls);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/controls/PlaybackControlsWs.tsx